import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, X, FileEdit, Type, Compass, Palette, Shapes, Globe2, Share2, Clapperboard, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProject } from '@/context/ProjectContext';
import BlueprintMark from '@/components/BlueprintMark';
import BusinessDescriptionPanel from '@/components/BusinessDescriptionPanel';
import NamingStudioPanel from '@/components/NamingStudioPanel';
import StrategyPanel from '@/components/StrategyPanel';
import BrandKitPanel from '@/components/BrandKitPanel';
import LogoStudioPanel from '@/components/LogoStudioPanel';
import WebsiteShellPanel from '@/components/WebsiteShellPanel';
import SocialStudioPanel from '@/components/SocialStudioPanel';
import VideoReelPanel from '@/components/VideoReelPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reuses the existing panels as step content -- each one is already a
// self-contained guided flow (intake -> generate -> review), so the wizard
// only adds sequencing/progress chrome around them, not new form logic.
// Business description comes first and is the one thing every later step
// pre-fills from -- describe it once here, not separately per tab.
const STEPS = [
  { id: 'business', label: 'Business', icon: FileEdit, Component: BusinessDescriptionPanel, isDone: (d) => !!d.project?.description },
  { id: 'naming', label: 'Naming', icon: Type, Component: NamingStudioPanel, isDone: (d) => (d.naming_candidates || []).length > 0 },
  { id: 'strategy', label: 'Strategy', icon: Compass, Component: StrategyPanel, isDone: (d) => !!d.strategy },
  { id: 'brand', label: 'Brand Kit', icon: Palette, Component: BrandKitPanel, isDone: (d) => !!d.brand_kit },
  { id: 'logo', label: 'Logo', icon: Shapes, Component: LogoStudioPanel, isDone: (d) => (d.logo_concepts || []).some((c) => c.variants) },
  { id: 'website', label: 'Website & App', icon: Globe2, Component: WebsiteShellPanel, isDone: (d) => !!d.site_content },
  { id: 'social', label: 'Social', icon: Share2, Component: SocialStudioPanel, isDone: (d) => (d.social_posts || []).length > 0 },
  { id: 'reels', label: 'Reels', icon: Clapperboard, Component: VideoReelPanel, isDone: (d) => (d.video_reels || []).length > 0 },
];

const ProjectWizard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, selectProject } = useProject();

  const [stepIndex, setStepIndex] = useState(0);
  const [status, setStatus] = useState({});

  const refreshStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/report/data`);
      setStatus(res.data);
    } catch (error) {
      console.error('Failed to load project status:', error);
    }
  }, [projectId]);

  useEffect(() => {
    if (!currentProject || currentProject.id !== projectId) {
      selectProject(projectId).catch(() => {
        toast.error('Project not found');
        navigate('/dashboard');
      });
    }
  }, [projectId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus, stepIndex]);

  const step = STEPS[stepIndex];
  const StepComponent = step.Component;
  const isLast = stepIndex === STEPS.length - 1;

  const goNext = () => {
    if (isLast) {
      navigate(`/dashboard/${projectId}/report`);
    } else {
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="project-wizard">
      {/* Header */}
      <header className="h-16 border-b border-border flex items-center justify-between px-6 bg-card shrink-0">
        <div className="flex items-center gap-3">
          <BlueprintMark className="w-6 h-6 text-primary" />
          <span className="font-heading font-semibold text-foreground">Guided Setup</span>
          {currentProject && <span className="text-muted-foreground hidden sm:inline">— {currentProject.name}</span>}
        </div>
        <Button variant="ghost" onClick={() => navigate(`/dashboard/${projectId}`)} data-testid="wizard-exit-btn">
          <X className="w-4 h-4 mr-1" />
          Exit to Dashboard
        </Button>
      </header>

      {/* Stepper */}
      <div className="border-b border-border bg-card px-6 py-4 shrink-0 overflow-x-auto">
        <div className="flex items-center gap-1 max-w-4xl mx-auto">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = s.isDone(status);
            const active = i === stepIndex;
            return (
              <div key={s.id} className="flex items-center flex-1 min-w-[110px]">
                <button
                  onClick={() => setStepIndex(i)}
                  className="flex flex-col items-center gap-1.5 flex-1"
                  data-testid={`wizard-step-${s.id}`}
                >
                  <div
                    className={`w-9 h-9 flex items-center justify-center border ${
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : done
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-black/[0.03] text-muted-foreground border-border'
                    }`}
                  >
                    {done && !active ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs ${active ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 ${done ? 'bg-primary/40' : 'bg-border'}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content -- the real panel, full height */}
      <div className="flex-1 overflow-hidden">
        <StepComponent />
      </div>

      {/* Footer controls */}
      <div className="border-t border-border bg-card px-6 py-4 flex items-center justify-between shrink-0">
        <Button
          variant="ghost"
          onClick={() => setStepIndex((i) => Math.max(i - 1, 0))}
          disabled={stepIndex === 0}
          className="disabled:opacity-40"
          data-testid="wizard-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <span className="text-xs text-muted-foreground font-mono">Step {stepIndex + 1} of {STEPS.length}</span>
        <Button onClick={goNext} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="wizard-next-btn">
          {isLast ? (
            <>
              <FileText className="w-4 h-4 mr-1.5" />
              Finish &amp; View Report
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProjectWizard;
